import { useMemo } from "react";
import { format } from "date-fns";

import { TarefaLogDTO } from "@/types/tarefa-log.type";
import {
  TarefaLogEventoMap,
  TarefaLogFiltrosCriteria,
  TarefaLogFiltrosOptions,
} from "@workflow/types";

import { Column } from "@/components/table/DataTable";
import { ServerDataTable } from "@/components/table/ServerDataTable";
import { useNavigationHelpers, ROUTES } from "@/lib/navigation-helpers";
import { tarefasQueries, tarefasKeys } from "@/services/api/queries/tarefas.queries";
import { TAREFA_LOG_EVENTO_CONFIG } from "@/components/tarefa/tarefa-log-look";
import { Person } from "@/components/shared/person";
import { IdentifierColumnTable } from "@/components/shared/IdentifierColumnTable";
import { cn } from "@/lib/utils";

const EventoCell = ({ log }: { log: TarefaLogDTO }) => {
  const config = TAREFA_LOG_EVENTO_CONFIG[log.evento];
  const Icon = config.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border border-transparent py-1 pl-1.5 pr-2 text-[11px] font-medium",
        config.tone,
      )}
    >
      <Icon className="h-3 w-3 shrink-0" />
      {config.label}
    </span>
  );
};

export function TarefaLogAdminListTable() {
  const { goToTarefaDetails } = useNavigationHelpers();

  const columns = useMemo<Column<TarefaLogDTO>[]>(
    () => [
      {
        key: "tarefa.id",
        title: "ID",
        sortable: true,
        width: "min-w-[60px]",
        render: (log) => (log.tarefa ? <IdentifierColumnTable identifier={log.tarefa.id} /> : "—"),
        raw: (log) => log.tarefa?.id?.toString() || "",
      },
      {
        key: "tarefa",
        title: "Tarefa",
        width: "w-[240px] max-w-[240px]",
        render: (log) =>
          log.tarefa ? (
            <span className="truncate text-sm font-medium text-foreground" title={log.tarefa.nome}>
              {log.tarefa.nome}
            </span>
          ) : (
            <span className="text-xs italic text-muted-foreground/70">Tarefa removida</span>
          ),
        raw: (log) => log.tarefa?.nome || "",
      },
      {
        key: "tarefaLog.evento",
        title: "Evento",
        sortable: true,
        width: "min-w-[180px]",
        render: (log) => <EventoCell log={log} />,
        raw: (log) => TarefaLogEventoMap[log.evento] || log.evento,
      },
      {
        key: "descricao",
        title: "Descrição",
        width: "min-w-[280px]",
        render: (log) => (
          <span className="line-clamp-1 text-sm text-muted-foreground" title={log.descricao}>
            {log.descricao}
          </span>
        ),
        raw: (log) => log.descricao,
      },
      {
        key: "ator",
        title: "Autor",
        width: "min-w-[200px]",
        render: (log) =>
          log.ator ? (
            <Person.Root pessoa={log.ator} asClickable className="flex items-center gap-2">
              <Person.Avatar />
              <Person.Name className="text-sm" />
            </Person.Root>
          ) : (
            <span className="text-xs italic text-muted-foreground/70">Sistema</span>
          ),
        raw: (log) => log.ator?.nome || "Sistema",
      },
      {
        key: "tarefaLog.criadoEm",
        title: "Data",
        sortable: true,
        width: "min-w-[160px]",
        render: (log) => (
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {log.criadoEm ? format(new Date(log.criadoEm), "dd/MM/yyyy HH:mm") : "—"}
          </span>
        ),
        raw: (log) => (log.criadoEm ? new Date(log.criadoEm).toISOString() : ""),
      },
    ],
    [],
  );

  return (
    <ServerDataTable<TarefaLogDTO, TarefaLogFiltrosOptions, TarefaLogFiltrosCriteria>
      config={{
        queryOptions: tarefasQueries.logsAdminList,
        filters: tarefasQueries.logsAdminFiltros,
        invalidateKey: tarefasKeys.logsAdmin,
      }}
      storageKey="tarefas-logs-admin"
      columns={columns}
      onRowClick={(log) => log.tarefa && goToTarefaDetails(log.tarefa.id)}
      getRowHref={(log) => (log.tarefa ? ROUTES.tarefaDetails(log.tarefa.id) : undefined)}
      emptyMessage="Nenhum evento encontrado."
      errorMessage="Não foi possível carregar o histórico. Tente recarregar."
      defaultSortColumn="tarefaLog.criadoEm"
      defaultSortOrder="DESC"
      searchPlaceholder="Pesquisar por tarefa, autor ou descrição..."
    />
  );
}
